import { useState, useEffect, useCallback, useRef } from 'react';
import { Coins, RefreshCw, Loader2, ShieldCheck, UserCheck } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useAuth } from '../contexts/AuthContext';
import { arDunningApi } from '../services/arDunningApi';
import { useToast } from '../components/ui/Toast';
import { ArKpiTiles } from '../components/ar-dunning/ArKpiTiles';
import { OverviewTab } from '../components/ar-dunning/OverviewTab';
import { OpenItemsTab } from '../components/ar-dunning/OpenItemsTab';
import { ArAgingTab } from '../components/ar-dunning/ArAgingTab';
import { FollowUpsTab } from '../components/ar-dunning/FollowUpsTab';
import { DunningHistoryTab } from '../components/ar-dunning/DunningHistoryTab';
import { ArSettingsTab } from '../components/ar-dunning/ArSettingsTab';
import { ItemDetailPanel } from '../components/ar-dunning/ItemDetailPanel';
import { ActionModal } from '../components/ar-dunning/ActionModal';
import type { ArStats, ArOpenItem, ArFilters, ArScope, ArCustomerRisk, ArCustomerRiskInput } from '../types/arDunning';

type Tab = 'overview' | 'open-items' | 'aging' | 'followups' | 'history' | 'settings';

const TABS: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'open-items', label: 'Open Items' },
    { key: 'aging', label: 'Aging' },
    { key: 'followups', label: 'Follow-ups' },
    { key: 'history', label: 'Dunning History' },
    { key: 'settings', label: 'Settings' },
];

const COMPANY_KEY = 'arDunning.company';
const BU_KEY = 'arDunning.businessUnit';
const CURRENCY_KEY = 'arDunning.consolidated';

export function AccountsReceivablePage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/accounts-receivable');
    const { user } = useAuth();
    const isAdmin = user?.isAdmin ?? false;
    const { toast } = useToast();

    const [company, setCompany] = useState<string>(() => { try { return localStorage.getItem(COMPANY_KEY) ?? ''; } catch { return ''; } });
    const [businessUnit, setBusinessUnit] = useState<string>(() => { try { return localStorage.getItem(BU_KEY) ?? ''; } catch { return ''; } });
    const [isConsolidated, setIsConsolidated] = useState<boolean>(() => { try { return localStorage.getItem(CURRENCY_KEY) !== 'false'; } catch { return true; } });

    const [filters, setFilters] = useState<ArFilters | null>(null);
    const [scope, setScope] = useState<ArScope | null>(null);
    const [stats, setStats] = useState<ArStats | null>(null);
    const [items, setItems] = useState<ArOpenItem[]>([]);
    const [itemsLoading, setItemsLoading] = useState(true);
    const [truncated, setTruncated] = useState(false);
    const [riskByCustomer, setRiskByCustomer] = useState<Map<string, ArCustomerRisk>>(new Map());

    const [tab, setTab] = useState<Tab>('overview');
    const [detailItem, setDetailItem] = useState<ArOpenItem | null>(null);
    const [actionItem, setActionItem] = useState<ArOpenItem | null>(null);
    const [preset, setPreset] = useState<{ bucket?: string; q?: string; flag?: '' | 'disputed' | 'promise' } | null>(null);
    const [presetNonce, setPresetNonce] = useState(0);
    const [focusSub, setFocusSub] = useState<'mine' | 'promises' | 'disputes'>('mine');
    const [focusNonce, setFocusNonce] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);

    // One-time filter/scope load.
    useEffect(() => {
        if (permLoading || !canView) return;
        void arDunningApi.getFilters()
            .then(f => { setFilters(f); setScope(f.scope); })
            .catch(() => { /* filters are best-effort; the lists still work */ });
    }, [permLoading, canView]);

    // Monotonic request ids: the initial "all" load and a company-change load run
    // concurrently (the all-load spans several pages), so only the LATEST request's
    // result may touch state — otherwise a slow/superseded load clobbers the current
    // selection and a superseded failure fires a spurious toast.
    const statsReqId = useRef(0);
    const itemsReqId = useRef(0);
    const riskReqId = useRef(0);

    const loadStats = useCallback(async () => {
        const reqId = ++statsReqId.current;
        try {
            const s = await arDunningApi.getStats(company || undefined, businessUnit || undefined);
            if (reqId === statsReqId.current) setStats(s);
        } catch (e) {
            if (reqId === statsReqId.current) toast(e instanceof Error ? e.message : 'Failed to load stats', 'error');
        }
    }, [company, businessUnit, toast]);

    // Per-customer payment-behaviour risk map. Open aggregates are computed here from the
    // already-loaded items (so the server never re-scans the heavy vwArOpenItem — a single
    // combined query hit the 15s SQL timeout); the server adds behaviour + limit + score.
    // Best-effort: badges degrade to grey if it fails.
    const loadRisk = useCallback(async (currentItems: ArOpenItem[]) => {
        const reqId = ++riskReqId.current;
        if (currentItems.length === 0) { setRiskByCustomer(new Map()); return; }
        const agg = new Map<string, ArCustomerRiskInput>();
        for (const i of currentItems) {
            const cur = agg.get(i.CustomerID) ?? {
                customerId: i.CustomerID, customerName: i.CustomerName, companyCode: i.CompanyCode,
                customerCode: i.CustomerCode, salesManagerId: i.SalesManagerID, salesManagerName: i.SalesManagerName,
                openEUR: 0, overdueEUR: 0, maxDaysOverdue: 0, openItemCount: 0, ribaExposureEUR: 0, hasOpenDishonored: false,
            };
            // Issued RiBa is a separate exposure figure — kept out of net open / overdue / aging.
            if (i.ItemCategory === 'RibaIssued') {
                cur.ribaExposureEUR = (cur.ribaExposureEUR ?? 0) + (i.OpenAmountEUR || 0);
            } else {
                cur.openEUR += i.OpenAmountEUR || 0;
                if (i.AgingBucket !== 'current' && i.AgingBucket !== 'unknown' && i.OpenAmountEUR > 0) cur.overdueEUR += i.OpenAmountEUR;
                cur.maxDaysOverdue = Math.max(cur.maxDaysOverdue, i.DaysOverdue || 0);
                if (i.ItemCategory === 'Dishonored') cur.hasOpenDishonored = true;
            }
            cur.openItemCount = (cur.openItemCount ?? 0) + 1;
            agg.set(i.CustomerID, cur);
        }
        try {
            const r = await arDunningApi.getCustomerRisk([...agg.values()], company || undefined);
            if (reqId === riskReqId.current) setRiskByCustomer(new Map(r.customers.map(c => [c.CustomerID, c])));
        } catch {
            if (reqId === riskReqId.current) setRiskByCustomer(new Map());
        }
    }, [company]);

    const loadItems = useCallback(async () => {
        const reqId = ++itemsReqId.current;
        setItemsLoading(true);
        try {
            const res = await arDunningApi.listAllOpenItems({ companyCode: company || undefined, businessUnit: businessUnit || undefined });
            if (reqId !== itemsReqId.current) return; // superseded by a newer load
            setItems(res.items);
            setScope(res.scope);
            setTruncated(res.truncated);
        } catch (e) {
            if (reqId === itemsReqId.current) toast(e instanceof Error ? e.message : 'Failed to load open items', 'error');
        } finally {
            if (reqId === itemsReqId.current) setItemsLoading(false);
        }
    }, [company, businessUnit, toast]);

    useEffect(() => {
        if (permLoading || !canView) return;
        void loadStats();
        void loadItems();
    }, [permLoading, canView, loadStats, loadItems]);

    // Risk is derived from the loaded items (recomputes whenever the item set changes).
    useEffect(() => {
        if (permLoading || !canView) return;
        void loadRisk(items);
    }, [items, permLoading, canView, loadRisk]);

    const refreshAll = () => { void loadStats(); void loadItems(); setReloadKey(k => k + 1); };
    // After an action/comment change: refresh the item enrichment + stats + follow-up worklist
    // (risk recomputes via the items effect once the reload lands).
    const onDataChanged = () => { void loadItems(); void loadStats(); setReloadKey(k => k + 1); };

    /** Distinct BUs with open items for a company ('' = all companies). */
    const businessUnitsFor = (code: string): string[] => {
        const list = filters?.businessUnits ?? [];
        const set = new Set((code ? list.filter(b => b.CompanyCode === code) : list).map(b => b.BusinessUnitCode));
        return [...set].sort();
    };

    const changeBusinessUnit = (bu: string) => {
        setBusinessUnit(bu);
        try { localStorage.setItem(BU_KEY, bu); } catch { /* ignore */ }
    };

    const changeCompany = (code: string) => {
        setCompany(code);
        try { localStorage.setItem(COMPANY_KEY, code); } catch { /* ignore */ }
        // Re-scope the BU selection: keep it if the new company has it; pre-select a
        // single-BU company's only BU (e.g. dpt -> SPC); otherwise fall back to all.
        const bus = businessUnitsFor(code);
        let nextBu = businessUnit && bus.includes(businessUnit) ? businessUnit : '';
        if (!nextBu && bus.length === 1) nextBu = bus[0];
        if (nextBu !== businessUnit) changeBusinessUnit(nextBu);
    };

    // Drop a persisted BU that is not valid for the current scope (stale localStorage,
    // or the BU no longer has open items) once the facet list is known.
    useEffect(() => {
        if (!filters || !businessUnit) return;
        const valid = (filters.businessUnits ?? []).some(b => b.BusinessUnitCode === businessUnit && (!company || b.CompanyCode === company));
        if (!valid) changeBusinessUnit('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, businessUnit, company]);
    const toggleCurrency = () => {
        setIsConsolidated(prev => {
            const next = !prev;
            try { localStorage.setItem(CURRENCY_KEY, String(next)); } catch { /* ignore */ }
            return next;
        });
    };

    const drillToOpenItems = (p: { bucket?: string; q?: string; flag?: '' | 'disputed' | 'promise' }) => {
        setPreset(p); setPresetNonce(n => n + 1); setTab('open-items');
    };
    const goFollowUps = (s: 'mine' | 'promises' | 'disputes') => {
        setFocusSub(s); setFocusNonce(n => n + 1); setTab('followups');
    };

    if (permLoading) {
        return <div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
    }
    if (!canView) {
        return (
            <div className="mx-auto max-w-md py-20 text-center">
                <h2 className="text-lg font-semibold text-slate-700">No access</h2>
                <p className="mt-2 text-sm text-slate-500">You don't have permission to view Accounts Receivable. Ask an admin to grant access in Settings.</p>
            </div>
        );
    }

    const companyOptions = [{ code: '', name: 'All companies' }, ...(filters?.companies ?? []).map(c => ({ code: c.CompanyCode, name: c.CompanyName }))];
    const buOptions = businessUnitsFor(company);
    const showSellerFilter = !!scope?.isCentral && (filters?.salesManagers.length ?? 0) > 1;
    const visibleTabs = TABS.filter(t => t.key !== 'settings' || isAdmin);

    return (
        <div className="mx-auto max-w-7xl px-4 py-5">
            {/* Header */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Coins className="h-6 w-6 text-indigo-600" />
                    <div>
                        <h1 className="text-xl font-semibold text-slate-800">Accounts Receivable &amp; Dunning</h1>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                            <select value={company} onChange={e => changeCompany(e.target.value)} className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:border-indigo-400 focus:outline-none">
                                {companyOptions.map(c => <option key={c.code} value={c.code}>{c.code ? `${c.name} (${c.code})` : c.name}</option>)}
                            </select>
                            <select value={businessUnit} onChange={e => changeBusinessUnit(e.target.value)} title="Business unit" className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:border-indigo-400 focus:outline-none">
                                <option value="">All BUs</option>
                                {buOptions.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                            {scope && (
                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${scope.isCentral ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'}`}>
                                    {scope.isCentral ? <><ShieldCheck className="h-3 w-3" /> Central</> : <><UserCheck className="h-3 w-3" /> My customers</>}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs font-medium">
                        <button onClick={() => { if (!isConsolidated) toggleCurrency(); }} className={`px-2.5 py-1.5 ${isConsolidated ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>EUR</button>
                        <button onClick={() => { if (isConsolidated) toggleCurrency(); }} className={`px-2.5 py-1.5 ${!isConsolidated ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>Original</button>
                    </div>
                    <button onClick={refreshAll} disabled={itemsLoading} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                        {itemsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
                    </button>
                </div>
            </div>

            {truncated && (
                <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Large data set — not all open items were loaded into the client views. Filter by company to see complete lists; the KPI tiles remain accurate (server-aggregated).
                </div>
            )}

            {/* KPI tiles (server-aggregated) */}
            <div className="mb-4">
                <ArKpiTiles
                    stats={stats}
                    onOverdueClick={() => drillToOpenItems({ bucket: 'overdue' })}
                    onPromisesClick={() => goFollowUps('promises')}
                    onDisputesClick={() => goFollowUps('disputes')}
                    onFollowUpsClick={() => goFollowUps('mine')}
                />
            </div>

            {/* Tabs */}
            <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200">
                {visibleTabs.map(t => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === t.key ? 'border-indigo-500 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'overview' && (
                <OverviewTab
                    stats={stats}
                    items={items}
                    riskByCustomer={riskByCustomer}
                    onDrillBucket={(b) => drillToOpenItems({ bucket: b })}
                    onDrillCustomer={(name) => drillToOpenItems({ q: name })}
                    onGoPromises={() => goFollowUps('promises')}
                    onGoDisputes={() => goFollowUps('disputes')}
                    onGoFollowUps={() => goFollowUps('mine')}
                />
            )}
            {tab === 'open-items' && (
                <OpenItemsTab
                    items={items}
                    loading={itemsLoading}
                    canEdit={canEdit}
                    isConsolidated={isConsolidated}
                    company={company}
                    companies={filters?.companies ?? []}
                    showSellerFilter={showSellerFilter}
                    riskByCustomer={riskByCustomer}
                    preset={preset}
                    presetNonce={presetNonce}
                    onOpenDetail={setDetailItem}
                    onOpenAction={setActionItem}
                    onChanged={onDataChanged}
                />
            )}
            {tab === 'aging' && (
                <ArAgingTab
                    items={items}
                    loading={itemsLoading}
                    onDrilldown={(name, bucket) => drillToOpenItems({ q: name, bucket: bucket ?? '' })}
                />
            )}
            {tab === 'followups' && (
                <FollowUpsTab
                    items={items}
                    canEdit={canEdit}
                    reloadKey={reloadKey}
                    focusSub={focusSub}
                    focusNonce={focusNonce}
                    onOpenDetail={setDetailItem}
                    onChanged={onDataChanged}
                />
            )}
            {tab === 'history' && <DunningHistoryTab />}
            {tab === 'settings' && isAdmin && <ArSettingsTab canEdit={canEdit} />}

            {/* Drill-downs: left-click = side panel, right-click = action modal */}
            {detailItem && (
                <ItemDetailPanel item={detailItem} canEdit={canEdit} companies={filters?.companies ?? []} risk={riskByCustomer.get(detailItem.CustomerID)} onClose={() => setDetailItem(null)} onChanged={onDataChanged} />
            )}
            {actionItem && !detailItem && (
                <ActionModal item={actionItem} canEdit={canEdit} onClose={() => setActionItem(null)} onSaved={onDataChanged} />
            )}
        </div>
    );
}

export default AccountsReceivablePage;
